<template>
    <div class="modal is-active">
        <div class="modal-background"></div>
        <div class="modal-card split-modal">
            <header class="modal-card-head">
                <p class="modal-card-title">Complete Parcels at Stop</p>
                <button class="delete" aria-label="close" @click="onClose"></button>
            </header>
            <section class="modal-card-body">
                <article v-if="requiresVerificationCode" class="message is-warning">
                    <div class="message-body">
                        This delivery requires a code from the consumer in order to proceed
                    </div>
                </article>
                <label class="label">Parcels to Complete*</label>
                <div v-if="parcels.length > 1" class="toggle-parcels">
                    <label class="help"
                        ><input
                            type="checkbox"
                            :checked="selectedParcelIds.length == parcels.length"
                            @click="onParcelToggleAll(parcels)"
                        />Select all</label
                    >
                </div>
                <div class="panel parcel-selector">
                    <label v-for="parcel in parcels" :key="parcel.id" class="panel-block">
                        <input
                            type="checkbox"
                            :checked="selectedParcelIds.includes(parcel.id)"
                            @click="onParcelToggle(parcel.id)"
                        />
                        <div>
                            <a :href="parcel.orderRef.carriersLink">{{ parcel.orderRef.token }}</a>
                            <div>
                                <p class="help parcel-barcode">
                                    {{ parcel.barcode }}
                                </p>
                                <p v-if="parcel.scanned" class="help parcel-scanned">
                                    Last scanned: {{ parcel.scanned.date.datetime }}
                                    {{ parcel.scanned.userName }}
                                </p>
                                <p v-if="!parcel.scanned" class="help parcel-scanned">
                                    Parcel has not yet been scanned
                                </p>
                            </div>
                        </div>
                    </label>
                </div>
                <div class="field">
                    <label class="label">Comment*</label>
                    <textarea
                        class="textarea"
                        placeholder="i.e. Why does TC need to complete this stop"
                        v-model="comment"
                    >
                    </textarea>
                </div>
                <div v-if="requiresVerificationCode">
                    <label class="label">Code*</label>
                    <div class="field is-grouped">
                        <p class="control has-icons-left has-icons-right">
                            <input
                                class="input"
                                placeholder="e.g. 1234"
                                v-model="verificationCode"
                                @keypress="isNumber($event)"
                                :minlength="VERIFICATION_CODE_LENGTH"
                                :maxlength="VERIFICATION_CODE_LENGTH"
                                :disabled="bypassVerificationCode"
                            />
                            <span class="icon is-small is-left">
                                <i class="fas fa-lock"></i>
                            </span>
                        </p>
                        <p class="control">
                            <label class="checkbox inline">
                                <input
                                    type="checkbox"
                                    v-model="bypassVerificationCode"
                                    @change="onByPassVerificationCodeToggle"
                                />
                                Deliver without a code
                            </label>
                        </p>
                    </div>
                    <article class="message is-info">
                        <div class="message-header">
                            <p v-if="!bypassVerificationCode">Deliver with code</p>
                            <p v-if="bypassVerificationCode">Deliver without code</p>
                        </div>
                        <div v-if="!bypassVerificationCode" class="message-body">
                            Use if the driver has received the code from the consumer but it says
                            wrong consumer code in the driver application. No report has to be made.
                        </div>
                        <div v-if="bypassVerificationCode" class="message-body">
                            Use if the driver already delivered the parcel without following the
                            instructions in the driver app. Write a QC# and notify Merchant success
                            team.
                        </div>
                    </article>
                </div>
            </section>
            <footer class="modal-card-foot pre-footer" v-if="status === 'PRE'">
                <button class="button is-danger" @click="onComplete" :disabled="!isSubmittable">
                    Complete
                </button>
                <button class="button is-pulled-left" @click="onClose">Cancel</button>
            </footer>
            <footer class="modal-card-foot in-progress-footer" v-if="status === 'IN_PROGRESS'">
                <img src="@/assets/loading-dots.svg" class="loading" />
                <strong>Completing Parcels</strong>
            </footer>
            <footer class="modal-card-foot completed-footer" v-if="status === 'COMPLETED'">
                <span class="tag is-success">
                    <span class="icon is-small">
                        <i class="fas fa-check"></i>
                    </span>
                    <span> Parcel have now been completed </span>
                </span>
                <button class="button is-pulled-left" @click="onClose">Close</button>
            </footer>
            <footer class="modal-card-foot failed-footer" v-if="status === 'FAILED'">
                <span class="tag is-danger">
                    <span class="icon is-small">
                        <i class="fas fa-exclamation-circle"></i>
                    </span>
                    <span>
                        Could not complete parcels at stop:
                        <span v-for="message in statusMessages" :key="message">
                            {{ message }}
                        </span>
                    </span>
                </span>
                <button class="button is-pulled-left" @click="onComplete">Try Again</button>
            </footer>
        </div>
    </div>
</template>
<script>
import { Webapi } from '@/Webapi'
import { statefor, getstate } from '@/utils/state'

const VERIFICATION_CODE_LENGTH = 4

export default {
    name: 'CompleteParcelsAtStopModal',
    props: {
        onClose: Function,
        parcels: Array,
        routeId: Number,
        stopId: Number,
        requiresVerificationCode: Boolean,
        resync: Function,
    },
    data: () => ({
        status: 'PRE',
        statusMessages: [],
        comment: '',
        selectedParcelIds: [],
        bypassVerificationCode: false,
        verificationCode: '',
        VERIFICATION_CODE_LENGTH,
    }),
    computed: {
        isSubmittable() {
            const {
                comment,
                selectedParcelIds,
                requiresVerificationCode,
                bypassVerificationCode,
                verificationCode,
            } = getstate(this)

            const isValid = comment && comment.length > 0 && selectedParcelIds.length > 0

            if (requiresVerificationCode) {
                return (
                    isValid &&
                    (bypassVerificationCode ||
                        (verificationCode && verificationCode.length === VERIFICATION_CODE_LENGTH))
                )
            }

            return isValid
        },
    },
    methods: {
        onComplete() {
            const state = statefor(this)
            const { routeId, stopId, selectedParcelIds, comment, verificationCode } = state()

            state({ status: 'IN_PROGRESS' })
            Webapi()
                .completeParcelsAtStop(
                    routeId,
                    stopId,
                    selectedParcelIds,
                    comment,
                    verificationCode || null,
                )
                .then((response) => this.onCompleteResponse(state, response))
        },
        onCompleteResponse(state, response) {
            const { status } = response
            const statusMessages = response.messages || []
            state({ status, statusMessages })
            state().resync()
        },
        onParcelToggle(parcelId) {
            const state = statefor(this)
            const { selectedParcelIds } = state()
            const selected = selectedParcelIds.includes(parcelId)
            if (selected) {
                state({ selectedParcelIds: selectedParcelIds.filter((id) => id !== parcelId) })
            } else {
                state({ selectedParcelIds: [...selectedParcelIds, parcelId] })
            }
        },
        onParcelToggleAll(parcels) {
            const state = statefor(this)
            const { selectedParcelIds } = state()
            const parcelsToToggle = []
            const selectAllParcels = selectedParcelIds.length !== parcels.length
            if (selectAllParcels) {
                parcels.forEach((parcel) => {
                    parcelsToToggle.push(parcel.id)
                })
            }
            state({
                selectedParcelIds: parcelsToToggle,
            })
        },
        onByPassVerificationCodeToggle() {
            const state = statefor(this)
            state({ verificationCode: '' })
        },
        isNumber(e) {
            const event = e || window.event
            const charCode = event.which ? event.which : event.keyCode
            if (charCode > 31 && (charCode < 48 || charCode > 57)) {
                event.preventDefault()
                return false
            }
            return true
        },
    },
}
</script>
<style lang="scss" scoped>
.parcel-selector {
    max-height: 400px;
    overflow-y: auto;
    border-top: solid thin #dadada;
    border-bottom: solid thin #dadada;
}

.pre-footer,
.completed-footer,
.failed-footer {
    display: flex;
    justify-content: space-between;
}

.loading {
    margin-top: 3px;
    height: 30px;
}

.checkbox.inline {
    line-height: 2.5;
}

.toggle-parcels {
    margin: 12px;
    label {
        font-weight: bold;
    }
    input {
        margin-right: 10px;
    }
}

.parcel-selector {
    label {
        div {
            width: 100%;
            div {
                width: auto;
                .parcel-barcode {
                    float: left;
                    margin-bottom: auto;
                }
                .parcel-scanned {
                    float: right;
                }
            }
        }
    }
}
</style>
